import { Component, inject } from '@angular/core';
import {
    FormArray,
    FormBuilder,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import {
    MAT_DIALOG_DATA,
    MatDialogModule,
    MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { DebugService } from '../../../services/debug.service';
import { TranslateModule } from '@ngx-translate/core';
import { AsnItemV3 } from '../../../../../api/addons-v3/models/asn-item-v-3';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
    selector: 'app-debug-asn-upload-dialog',
    templateUrl: './debug-asn-upload-dialog.component.html',
    styleUrl: './debug-asn-upload-dialog.component.scss',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatNativeDateModule,
        MatButtonModule,
        MatIconModule,
        MatDialogModule,
        TranslateModule,
        MatTooltip,
    ],
})
export class DebugAsnUploadDialogComponent {
    asnForm: FormGroup;
    private fb = inject(FormBuilder);
    private dialogRef = inject(MatDialogRef<DebugAsnUploadDialogComponent>);
    private debugService = inject(DebugService);
    public data: DebugAsnUploadInputData = inject(MAT_DIALOG_DATA);

    constructor() {
        this.asnForm = this.fb.group({
            organizationSiteKey: this.fb.control(
                this.data.organizationSiteKey,
                [Validators.required]
            ),
            items: this.fb.array([this.createAsnItemFormGroup()]),
        });
    }

    private createAsnItemFormGroup(): FormGroup {
        return this.fb.group({
            advancedShippingNotice: ['', [Validators.required]],
            articleOrderNumber: ['', [Validators.required]],
            consigneeOrderNumber: [''],
            description: [''],
            quantity: [1, [Validators.required, Validators.min(1)]],
            sellableCode: ['', [Validators.required]],
            storageLocation: [''],
        });
    }

    get itemsArray(): FormArray {
        return this.asnForm.get('items') as FormArray;
    }

    addItem(): void {
        this.itemsArray.push(this.createAsnItemFormGroup());
    }

    removeItem(index: number): void {
        if (this.itemsArray.length > 1) {
            this.itemsArray.removeAt(index);
        }
    }

    async onSubmit(): Promise<void> {
        if (this.asnForm.invalid) {
            this.asnForm.markAllAsTouched();
            return;
        }

        const resultData = {
            organizationSiteKey: this.asnForm.get('organizationSiteKey')
                ?.value as string,
            items: this.itemsArray.value as AsnItemV3[],
        } as DebugAsnUploadResultData;

        if (this.debugService.isLoggingEnabled()) {
            console.log(
                '[DebugService] Submitting ASN update with payload:',
                resultData
            );
        }

        this.dialogRef.close(resultData);
    }

    onCancel(): void {
        this.dialogRef.close();
    }
}

export interface DebugAsnUploadInputData {
    organizationSiteKey: string;
}

export interface DebugAsnUploadResultData {
    organizationSiteKey: string;
    items: AsnItemV3[];
}
